import { User } from 'firebase/auth';
import { firebaseStripeService } from '../firebase-stripe.service';
import { subscriptionService, SubscriptionType } from './subscription.service';
import { userService } from '../firebase/user.service';
import { env } from '../../config/env';

interface StripeSubscriptionData {
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing' | 'unpaid';
  priceId: string;
  subscriptionType: SubscriptionType;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
}

class StripeSyncService {
  constructor() {
    // Utilise le service existant
  }

  /**
   * Synchroniser l'abonnement Stripe avec Firebase au login
   */
  async syncSubscriptionOnLogin(user: User): Promise<void> {
    try {
      // Vérifier si l'utilisateur a des abonnements Stripe actifs
      const stripeSubscription = await this.getActiveStripeSubscription(user.uid);

      if (stripeSubscription) {
        // Récupérer l'abonnement actuel dans Firebase
        const currentFirebaseSubscription = await subscriptionService.getCurrentSubscription(user.uid);

        // Comparer et mettre à jour si nécessaire
        if (currentFirebaseSubscription.type !== stripeSubscription.subscriptionType) {
          await this.updateFirebaseSubscription(user.uid, stripeSubscription);
        } else {}

        // Mettre à jour les métadonnées d'abonnement
        await this.updateSubscriptionMetadata(user.uid, stripeSubscription);
      } else {
        // Vérifier si l'utilisateur a un abonnement payant dans Firebase mais pas dans Stripe
        const currentFirebaseSubscription = await subscriptionService.getCurrentSubscription(user.uid);

        if (currentFirebaseSubscription.type !== 'free') {}
      }
    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la synchronisation:`, error);
      // Ne pas lever l'erreur pour éviter de bloquer le login
    }
  }

  /**
   * Récupérer l'abonnement Stripe actif pour un utilisateur
   */
  private async getActiveStripeSubscription(userId: string): Promise<StripeSubscriptionData | null> {
    try {
      firebaseStripeService.setUser(userId);
      const activeSubscription = await firebaseStripeService.getActiveSubscription(userId);

      if (!activeSubscription || activeSubscription.status !== 'active') {
        return null;
      }

      // Déterminer le type d'abonnement basé sur le priceId
      const subscriptionType = this.mapPriceIdToSubscriptionType(activeSubscription.priceId);

      return {
        status: activeSubscription.status,
        priceId: activeSubscription.priceId,
        subscriptionType,
        currentPeriodEnd: activeSubscription.currentPeriodEnd,
        cancelAtPeriodEnd: activeSubscription.cancelAtPeriodEnd
      };

    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la récupération de l'abonnement Stripe:`, error);
      return null;
    }
  }

  /**
   * Mapper un priceId Stripe vers un type d'abonnement
   */
  private mapPriceIdToSubscriptionType(priceId: string): SubscriptionType {
    // Mapping basé sur les prix configurés (Price IDs réels)
    const priceMapping: Record<string, SubscriptionType> = {
      'price_essential_monthly': 'essential',
      'price_pro_monthly': 'pro',
      // Price IDs réels de production
      'price_1RYBu2JTjTLaR8wtLr6wZ2BX': 'essential', // Essential Plan
      'price_1RYBvWJTjTLaR8wtxbMHKfnS': 'pro',       // Pro Plan
    };

    // Vérifier le mapping direct d'abord
    if (priceMapping[priceId]) {
      return priceMapping[priceId];
    }

    // Vérifier par variables d'environnement
    const essentialPriceId = env.stripe.essentialPriceId;
    const proPriceId = env.stripe.proPriceId;

    if (priceId === essentialPriceId) {
      return 'essential';
    }
    if (priceId === proPriceId) {
      return 'pro';
    }

    // Mapping par défaut basé sur le nom du prix
    if (priceId.includes('essential')) {
      return 'essential';
    }
    if (priceId.includes('pro')) {
      return 'pro';
    }

    // Par défaut, considérer comme essential si on ne peut pas déterminer
    console.warn(`⚠️ [StripeSyncService] PriceId non reconnu: ${priceId}, utilisation d'essential par défaut`);
    return 'essential';
  }

  /**
   * Mettre à jour l'abonnement Firebase avec les données Stripe
   */
  private async updateFirebaseSubscription(userId: string, stripeData: StripeSubscriptionData): Promise<void> {
    try {
      await subscriptionService.updateSubscription(userId, stripeData.subscriptionType);

      // Préparer les métadonnées en excluant les valeurs undefined
      const metadata: Record<string, any> = {
        stripeSubscriptionStatus: stripeData.status,
        lastStripeSyncAt: new Date()
      };

      // Ajouter seulement les valeurs définies
      if (stripeData.currentPeriodEnd !== undefined) {
        metadata['stripeCurrentPeriodEnd'] = stripeData.currentPeriodEnd;
      }

      if (stripeData.cancelAtPeriodEnd !== undefined) {
        metadata['stripeCancelAtPeriodEnd'] = stripeData.cancelAtPeriodEnd;
      }

      // Mettre à jour les métadonnées d'abonnement
      await userService.updateUserProfile(userId, metadata);

    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la mise à jour Firebase:`, error);
      throw error;
    }
  }

  /**
   * Mettre à jour les métadonnées d'abonnement même si le type est identique
   */
  private async updateSubscriptionMetadata(userId: string, stripeData: StripeSubscriptionData): Promise<void> {
    try {
      // Préparer les métadonnées en excluant les valeurs undefined
      const metadata: Record<string, any> = {
        stripeSubscriptionStatus: stripeData.status,
        lastStripeSyncAt: new Date()
      };

      // Ajouter seulement les valeurs définies
      if (stripeData.currentPeriodEnd !== undefined) {
        metadata['stripeCurrentPeriodEnd'] = stripeData.currentPeriodEnd;
      }

      if (stripeData.cancelAtPeriodEnd !== undefined) {
        metadata['stripeCancelAtPeriodEnd'] = stripeData.cancelAtPeriodEnd;
      }

      await userService.updateUserProfile(userId, metadata);
    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la mise à jour des métadonnées:`, error);
    }
  }

  /**
   * Forcer une synchronisation manuelle
   */
  async forceSyncSubscription(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const stripeSubscription = await this.getActiveStripeSubscription(userId);

      if (stripeSubscription) {
        await this.updateFirebaseSubscription(userId, stripeSubscription);
        return {
          success: true,
          message: `Abonnement synchronisé: ${stripeSubscription.subscriptionType}`
        };
      } else {
        return {
          success: true,
          message: 'Aucun abonnement Stripe actif trouvé'
        };
      }
    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la synchronisation forcée:`, error);
      return {
        success: false,
        message: `Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      };
    }
  }

  /**
   * Vérifier la cohérence entre Stripe et Firebase
   */
  async checkSubscriptionConsistency(userId: string): Promise<{
    isConsistent: boolean;
    firebaseSubscription: SubscriptionType;
    stripeSubscription: SubscriptionType | null;
    recommendation?: string;
  }> {
    try {
      const [firebaseSubscription, stripeSubscription] = await Promise.all([
        subscriptionService.getCurrentSubscription(userId),
        this.getActiveStripeSubscription(userId)
      ]);

      const firebaseType = firebaseSubscription.type;
      const stripeType = stripeSubscription?.subscriptionType || null;

      const isConsistent = firebaseType === stripeType ||
        (firebaseType === 'free' && stripeType === null);

      let recommendation = '';
      if (!isConsistent) {
        if (stripeType && firebaseType === 'free') {
          recommendation = 'Mettre à jour Firebase avec l\'abonnement Stripe actif';
        } else if (!stripeType && firebaseType !== 'free') {
          recommendation = 'Vérifier pourquoi l\'abonnement Stripe n\'est pas actif';
        } else {
          recommendation = 'Synchroniser les deux systèmes';
        }
      }

      const result: {
        isConsistent: boolean;
        firebaseSubscription: SubscriptionType;
        stripeSubscription: SubscriptionType | null;
        recommendation?: string;
      } = {
        isConsistent,
        firebaseSubscription: firebaseType,
        stripeSubscription: stripeType
      };

      if (recommendation) {
        result.recommendation = recommendation;
      }

      return result;

    } catch (error) {
      console.error(`❌ [StripeSyncService] Erreur lors de la vérification de cohérence:`, error);
      throw error;
    }
  }
}

export const stripeSyncService = new StripeSyncService();
export default stripeSyncService; 